import React, { useEffect, useRef, useState, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton,
  Alert,
  Button,
  Fab,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  ThemeProvider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { voverTheme } from '@/theme';
import { 
  MoreVert,
  RocketLaunch
} from '@mui/icons-material';
import Logo from './components/Logo';
import Login from './pages/Login';
import CondominiumProductPage from './pages/CondominiumProductPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ContactPage from './pages/ContactPage';
import AccountDeletionInfoPage from './pages/AccountDeletionInfoPage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import Unauthorized from './pages/Unauthorized';
import PaymentPage from './pages/PaymentPage';
import GoFinancePage from './pages/GoFinancePage';
import ErrorBoundary from './components/ErrorBoundary';
import { DemandsProvider, useDemands } from './contexts/DemandsContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { AuthProvider } from './contexts/AuthContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { useMenuItems } from './components/DynamicMenu';
import { SnackbarProvider } from './components/SnackbarNotification';
import GlobalLoading from './components/GlobalLoading';
import { ROUTES } from '@/constants';
import { AppRoutes } from './routes';
import UserMenu from './components/UserMenu';
import NotificationBell from './components/NotificationBell';
import DemandFilters from '@/features/demands/components/DemandFilters';
import DemandsSearchField from '@/features/demands/components/DemandsSearchField';
import ReportsFilters from '@/features/reports/ReportsFilters';
import ClientOnlyFilters from '@components/ClientOnlyFilters';
import RefreshButton from './components/RefreshButton';
import MobileFiltersDrawer from './components/MobileFiltersDrawer';
import ImpersonationBanner from './components/ImpersonationBanner';
import TrialPeriodBanner from './components/TrialPeriodBanner';
import PaymentDueReminderBar from './components/PaymentDueReminderBar';
import MobileAppNudgeBanner from './components/MobileAppNudgeBanner';
import { lazyPages } from '@config/lazyImports';
import ProtectedRoute from './components/ProtectedRoute';
import { apiService } from '@services/api';
import { useAuth } from '@contexts/AuthContext';
import { SpotlightOverlay } from '@/components/onboarding/SpotlightOverlay';
import { UserRole } from '@/types';

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
function AppContent() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openBanner, setOpenBanner] = useState<'impersonation' | 'trial' | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const [logoMissing, setLogoMissing] = useState(false);
  const [onboardingPending, setOnboardingPending] = useState(false);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const handleBannerOpen = (banner: 'impersonation' | 'trial') => {
    setOpenBanner(prev => prev === banner ? null : banner); // Fecha se já estiver aberto, abre se não estiver
  };
  
  const handleBannerClose = () => setOpenBanner(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = useMenuItems();
  // Pedido do usuário (04/09): divisão clara entre páginas vinculadas a um condomínio e páginas
  // gerais do tenant — ver PageConfig.scope (config/pages.tsx). Cada grupo só é considerado se
  // sobrar pelo menos 1 item depois do filtro de permissão de useMenuItems() acima, pra não
  // renderizar um rótulo de seção vazio para papéis sem nenhuma permissão daquele grupo.
  const clientScopeMenuItems = menuItems.filter((item) => item.scope === 'client');
  const globalScopeMenuItems = menuItems.filter((item) => item.scope === 'global');

  // A página de demandas é lazy (code-split, ver lazyImports.ts) para não inchar o bundle
  // principal, mas também é a tela de pouso de todo usuário autenticado — sem isso, o
  // Suspense só dispara o download do chunk quando a rota já está renderizando, criando um
  // espinho visível de carregamento. Aqui a gente adianta o download em paralelo assim que a
  // sessão resolve, sem bloquear nada (chunk já costuma estar em cache quando o Suspense pede).
  useEffect(() => {
    if (!user || authLoading) return;
    import('./features/demands/DemandsBoard');
  }, [user, authLoading]);

  // Verificar se é uma página pública
  const isPublicPage =
    location.pathname.startsWith(ROUTES.PUBLIC_DEMAND) ||
    location.pathname.startsWith(`${ROUTES.PUBLIC_DEMAND}/`);

  const showMobileNudge = !isPublicPage && location.pathname.startsWith(ROUTES.APP);

  useEffect(() => {
    let cancelled = false;
    if (!user || authLoading) return;
    if (!location.pathname.startsWith(ROUTES.APP)) return;
    const canPrompt = user.role === UserRole.ADMIN || user.isSystemAdmin === true;
    if (!canPrompt) return;
    (async () => {
      try {
        const res = await apiService.getTenantInfo();
        const t = res.data?.data ?? res.data;
        const hasLogo = !!(t?.logo && String(t.logo).trim());
        if (!cancelled) setLogoMissing(!hasLogo);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading, location.pathname]);

  // Verificar se deve mostrar os filtros de demandas (Demandas, Relatórios, Sugestões,
  // Checklists e Patrimônio — pedido do usuário, 03/09: as duas últimas usam o mesmo filtro do
  // cabeçalho, só com a opção de condomínio, ver ClientOnlyFilters). Pedido do usuário (04/09):
  // QR Codes, Demandas Recorrentes, Caixa de Sugestões e Painel Público entram no mesmo grupo —
  // também são páginas condomínio-scoped, mesmo filtro de condomínio.
  const showDemandFilters =
    location.pathname === ROUTES.APP_DEMANDS ||
    location.pathname === ROUTES.APP_REPORTS ||
    location.pathname === ROUTES.APP_CHECKLISTS ||
    location.pathname === ROUTES.APP_PATRIMONY ||
    location.pathname === ROUTES.APP_QR_CODES ||
    location.pathname === ROUTES.APP_RECURRING_DEMANDS ||
    location.pathname === ROUTES.APP_SUGGESTION_BOX ||
    location.pathname === ROUTES.APP_PUBLIC_PANEL;
  
  // Acessar o contexto de demandas
  const { refreshDemands } = useDemands();

  const readOnboardingFlag = (key: string) => {
    try {
      return localStorage.getItem(key) === '1' || sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  };

  const [onboardingGuideActive, setOnboardingGuideActiveState] = useState<boolean>(() => readOnboardingFlag('onboardingGuideActive'));
  const [onboardingSkipped, setOnboardingSkippedState] = useState<boolean>(() => readOnboardingFlag('onboardingGuideSkipped'));

  const ctaRef = useRef<HTMLDivElement | null>(null);
  const userId = user?._id;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (authLoading) return;
      if (!userId) return;
      try {
        const res = await apiService.getOnboardingStatus();
        const completed = !!res.data?.data?.completed;
        if (!cancelled) setOnboardingPending(!completed);
        if (completed) {
          try {
            localStorage.removeItem('onboardingGuideActive');
            sessionStorage.removeItem('onboardingGuideActive');
          } catch {
            // ignore
          }
          if (!cancelled) {
            setOnboardingGuideActiveState(false);
            setOnboardingSkippedState(false);
          }
        }
      } catch {
        // Não "sumir" CTA se o backend de status falhar — o estado instantâneo (user.onboarding)
        // já nos diz se está pendente. Aqui só evitamos sobrescrever para false.
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [authLoading, userId, onboardingSkipped]);

  // Fonte instantânea (sem flash): usar flag do usuário vinda do /auth/verify quando disponível
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    const onboardingKnown = user.onboarding !== undefined && user.onboarding !== null;
    if (!onboardingKnown) {
      // Se o backend ainda não enviou o campo onboarding no profile,
      // não "assuma pendente" (evita flash para quem já concluiu).
      return;
    }

    const completed = user.onboarding?.completed === true;
    const forceShow = user.onboarding?.forceShow === true;
    // Se completed=true e não está forçando, desliga CTA imediatamente
    if (completed && !forceShow) {
      setOnboardingPending(false);
      return;
    }
    // Se não está completed (ou forceShow=true), marca pendente e garante que flags locais não escondam o onboarding.
    setOnboardingPending(true);
    if (onboardingSkipped) {
      try {
        localStorage.removeItem('onboardingGuideSkipped');
        sessionStorage.removeItem('onboardingGuideSkipped');
      } catch {
        // ignore
      }
      setOnboardingSkippedState(false);
    }
  }, [authLoading, user, onboardingSkipped]);

  const pulse = keyframes`
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(25,118,210,0.35); }
    60% { transform: scale(1.04); box-shadow: 0 0 0 10px rgba(25,118,210,0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(25,118,210,0); }
  `;

  // CTA só aparece ANTES de ativar o guia (spotlight inicial).
  // Depois que o usuário clicar "Comece por aqui", o CTA some e o spotlight passa a ser contextual nas telas.
  // Acesso guiado restrito a administradores (pedido do usuário, 16/09, mesmo ajuste do mobile —
  // ver isAdminRole em mobile/lib/auth-context.tsx): o roteiro é fixo (criar condomínio → QR codes
  // → exportar → demanda recorrente → demanda simples), sem levar em conta o que o papel do
  // usuário pode acessar — um papel sem permissão de `clients` era mandado pro primeiro passo
  // mesmo assim. Até existir um roteiro por papel, o acesso guiado fica restrito a quem é admin.
  // Único ponto de ativação da flag `onboardingGuideActive` é o botão abaixo — bloqueando aqui,
  // as dicas contextuais em Demandas/Condomínios (useDemandsBoardOnboarding/useClientOnboardingGuide,
  // que só leem essa flag) também ficam inativas para quem não é admin.
  const isAdminUser = user?.role === UserRole.ADMIN;
  const shouldShowOnboardingCta = isAdminUser && !onboardingGuideActive && !onboardingSkipped && onboardingPending;

  // Se for página pública, de login, unauthorized ou home, não mostrar a barra de navegação
  // Essas rotas são tratadas no App principal, então não precisamos renderizá-las aqui
  if (
    isPublicPage ||
    location.pathname === ROUTES.LOGIN ||
    location.pathname === ROUTES.UNAUTHORIZED ||
    location.pathname === ROUTES.HOME
  ) {
    // Essas rotas são tratadas no nível superior do App
    return null;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(90deg, #f7f8fc 0%, #d1d8e4 100%)',
          color: '#334155',
          borderBottom: '1px solid #e2e8f0',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          top: 0, // Garantir que fique abaixo do banner quando ele aparecer
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1, sm: 2 },
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Logo - icon em mobile, logo em desktop. Pedido do usuário: Ctrl/Cmd+click deve abrir
              numa nova aba — component={RouterLink} renderiza um <a href> de verdade (ver mesmo
              comentário no menu principal, acima). */}
          <Box
            component={RouterLink}
            to={ROUTES.APP}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mr: { xs: 1, sm: 2 },
              minWidth: { xs: 'auto', sm: 'auto' },
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Logo 
              height={isMobile ? 40 : 52}
              variant="auto"
              color="default"
              sx={{ mr: { xs: 0.5, sm: 1 } }}
            />
            {/* Texto VOVER removido quando usa logo completa (desktop) */}
            {isMobile && (
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: '#334155'
                }}
              >
                VOVER
              </Typography>
            )}
          </Box>

          <Box sx={{ flexGrow: 1 }} />
          
          {/* Filtros - apenas em desktop */}
          {!isMobile && showDemandFilters && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2, height: '100%' }}>
              {location.pathname === ROUTES.APP_REPORTS ? (
                <ReportsFilters />
              ) : location.pathname === ROUTES.APP_CHECKLISTS ||
                  location.pathname === ROUTES.APP_PATRIMONY ||
                  location.pathname === ROUTES.APP_QR_CODES ||
                  location.pathname === ROUTES.APP_RECURRING_DEMANDS ||
                  location.pathname === ROUTES.APP_SUGGESTION_BOX ||
                  location.pathname === ROUTES.APP_PUBLIC_PANEL ? (
                <ClientOnlyFilters />
              ) : (
                <>
                  <DemandsSearchField />
                  <DemandFilters />
                </>
              )}
            </Box>
          )}
          
          {/* Botão de Refresh - menor em mobile */}
          <RefreshButton onRefresh={refreshDemands} />

          {/* Indicadores (padronizado): antes do avatar (UserMenu) */}
          <ImpersonationBanner
            isMobile={isMobile}
            isOpen={openBanner === 'impersonation'}
            onOpen={() => handleBannerOpen('impersonation')}
            onClose={handleBannerClose}
          />

          <TrialPeriodBanner
            isMobile={isMobile}
            isOpen={openBanner === 'trial'}
            onOpen={() => handleBannerOpen('trial')}
            onClose={handleBannerClose}
          />
          
          <IconButton
            size={isMobile ? "small" : "large"}
            aria-label="menu"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            onClick={handleMenuClick}
            color="inherit"
            sx={{ mr: { xs: 0.5, sm: 1 }, color: '#334155' }}
          >
            <MoreVert fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {clientScopeMenuItems.length > 0 && [
              <ListSubheader key="client-scope-header">Do condomínio</ListSubheader>,
              ...clientScopeMenuItems.map((item) => (
                // Pedido do usuário: Ctrl/Cmd+click (ou clique do meio) deve abrir numa nova aba,
                // como é padrão na web — só funciona com um <a href> de verdade no DOM, não com
                // onClick+navigate() programático. `component={RouterLink} to={...}` faz o
                // MenuItem renderizar um link real; o próprio RouterLink já detecta clique
                // modificado e deixa o navegador tratar nativamente (não intercepta com
                // preventDefault nesse caso).
                <MenuItem key={item.path} component={RouterLink} to={item.path} onClick={handleMenuClose}>
                  <ListItemIcon>
                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                  </ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              )),
            ]}

            {clientScopeMenuItems.length > 0 && globalScopeMenuItems.length > 0 && <Divider key="scope-divider" />}

            {globalScopeMenuItems.length > 0 && [
              <ListSubheader key="global-scope-header">Geral</ListSubheader>,
              ...globalScopeMenuItems.map((item) => (
                <MenuItem key={item.path} component={RouterLink} to={item.path} onClick={handleMenuClose}>
                  <ListItemIcon>
                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                  </ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              )),
            ]}
          </Menu>

          {/* Central de Notificações (sino, pedido do usuário 05/09) — mesmo lugar-padrão dos
              outros indicadores, entre o menu de atalhos e o avatar. */}
          <NotificationBell />

          <UserMenu />
        </Toolbar>
      </AppBar>

      <PaymentDueReminderBar />
      {showMobileNudge && <MobileAppNudgeBanner deepLinkUrl="vover://" />}
      {logoMissing && (
        <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1 }}>
          <Alert
            severity="warning"
            onClose={() => setLogoMissing(false)}
            action={
              <Button color="inherit" size="small" component={RouterLink} to={ROUTES.APP_CONFIG}>
                Ir para Visão geral
              </Button>
            }
          >
            Seu cadastro está sem <strong>logo</strong>. Atualize em <strong>Configurações → Visão Geral</strong>.
          </Alert>
        </Box>
      )}

      <Box sx={{ mt: { xs: 7, sm: 8 } }}>
        <AppRoutes />
      </Box>

      {/* Drawer de filtros mobile */}
      <MobileFiltersDrawer showDemandFilters={showDemandFilters} />

      {/* CTA flutuante para primeiro acesso guiado (desktop + mobile responsivo) */}
      {shouldShowOnboardingCta ? (
        <Box
          sx={{
            position: 'fixed',
            // Quando spotlight está ativo, o CTA precisa ficar acima do overlay.
            zIndex: shouldShowOnboardingCta && !onboardingGuideActive ? 2200 : 1400,
            bottom: { xs: 16, md: 24 },
            left: { xs: 16, md: 'auto' },
            right: { xs: 16, md: 24 },
            display: 'flex',
            justifyContent: { xs: 'stretch', md: 'flex-end' },
          }}
        >
          <Tooltip title="Primeiro acesso guiado">
            <Box ref={ctaRef} sx={{ display: 'inline-flex', width: { xs: '100%', md: 'auto' } }}>
              <Fab
                variant="extended"
                color="primary"
                onClick={() => {
                  try {
                    localStorage.setItem('onboardingGuideActive', '1');
                    localStorage.removeItem('onboardingGuideSkipped');
                  } catch {
                    sessionStorage.setItem('onboardingGuideActive', '1');
                    sessionStorage.removeItem('onboardingGuideSkipped');
                  }
                  setOnboardingGuideActiveState(true);
                  setOnboardingSkippedState(false);
                  navigate('/app/clients');
                }}
                sx={{
                  width: { xs: '100%', md: 'auto' },
                  px: 2.5,
                  borderRadius: 999,
                  boxShadow: 6,
                  fontWeight: 800,
                  textTransform: 'none',
                  animation: `${pulse} 1.25s ease-in-out infinite`,
                }}
              >
                <RocketLaunch sx={{ mr: 1 }} />
                Comece por aqui
              </Fab>
            </Box>
          </Tooltip>
        </Box>
      ) : null}

      {/* Spotlight imediato no primeiro acesso (ex.: tela de Demandas) */}
      {shouldShowOnboardingCta ? (
        <SpotlightOverlay
          open
          targetRef={ctaRef as React.RefObject<HTMLElement | null>}
          title="Primeiro acesso guiado"
          description="Clique no botão destacado para iniciar o onboarding e seguir o passo a passo."
          onSkip={() => {
            (async () => {
              try {
                await apiService.completeOnboarding();
              } catch {
                // ignore
              } finally {
                try {
                  localStorage.removeItem('onboardingGuideActive');
                  sessionStorage.removeItem('onboardingGuideActive');
                  localStorage.removeItem('onboardingGuideSkipped');
                  sessionStorage.removeItem('onboardingGuideSkipped');
                } catch {
                  // ignore
                }
                setOnboardingGuideActiveState(false);
                setOnboardingSkippedState(false);
                setOnboardingPending(false);
              }
            })();
          }}
        />
      ) : null}
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={voverTheme}>
    <ErrorBoundary>
      <Router>
        <LoadingProvider>
          <SnackbarProvider>
            <ConfigProvider>
              <AuthProvider>
              <Routes>
                {/* Rotas públicas - sem autenticação */}
                <Route path={ROUTES.HOME} element={<CondominiumProductPage />} />
                <Route path="/home" element={<CondominiumProductPage />} />
                <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
                <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path={ROUTES.CONTACT} element={<ContactPage />} />
                <Route path={ROUTES.ACCOUNT_DELETION_INFO} element={<AccountDeletionInfoPage />} />
                <Route path="/exclusao-de-conta" element={<AccountDeletionInfoPage />} />
                <Route path={ROUTES.LOGIN} element={<Login />} />
                <Route path="/reset-password" element={
                  <Suspense fallback={<div>Carregando...</div>}>
                    <lazyPages.ResetPassword />
                  </Suspense>
                } />
                <Route path={ROUTES.UNAUTHORIZED} element={<Unauthorized />} />
                <Route path={ROUTES.PAYMENT} element={<PaymentPage />} />
                <Route path={ROUTES.GO_FINANCE} element={<GoFinancePage />} />
                <Route path={ROUTES.SYSTEM_ADMIN_SELECT} element={
                  <Suspense fallback={<div>Carregando...</div>}>
                    <lazyPages.SystemAdminSelection />
                  </Suspense>
                } />
                <Route
                  path={ROUTES.SYSTEM_ADMIN_AUDIT}
                  element={
                    <Suspense fallback={<div>Carregando...</div>}>
                      <ProtectedRoute systemAdminOnly>
                        <lazyPages.PlatformAuditPage />
                      </ProtectedRoute>
                    </Suspense>
                  }
                />
                <Route
                  path={`${ROUTES.PUBLIC_DEMAND}/:token`}
                  element={
                    <Suspense fallback={<div>Carregando...</div>}>
                      <lazyPages.PublicDemandForm />
                    </Suspense>
                  }
                />

                <Route
                  path={`${ROUTES.PUBLIC_SUGGESTION_BOX}/:token`}
                  element={
                    <Suspense fallback={<div>Carregando...</div>}>
                      <lazyPages.PublicSuggestionForm />
                    </Suspense>
                  }
                />

                <Route
                  path={`${ROUTES.PUBLIC_OPERATION_REPORT}/:token`}
                  element={
                    <Suspense fallback={<div>Carregando...</div>}>
                      <lazyPages.PublicOperationReport />
                    </Suspense>
                  }
                />

                <Route
                  path={`${ROUTES.PROVIDER_COMPLETION_LINK}/:token`}
                  element={
                    <Suspense fallback={<div>Carregando...</div>}>
                      <lazyPages.ProviderCompletionLinkPage />
                    </Suspense>
                  }
                />

                {/* Rotas privadas - com autenticação */}
                <Route path={`${ROUTES.APP}/*`} element={
                  <DemandsProvider>
                    <AppContent />
                  </DemandsProvider>
                } />
                <Route path={ROUTES.ACCOUNT_DELETION} element={
                  <ProtectedRoute>
                    <AccountDeletionPage />
                  </ProtectedRoute>
                } />
              </Routes>
              </AuthProvider>
            </ConfigProvider>
          </SnackbarProvider>
          <GlobalLoading />
        </LoadingProvider>
      </Router>
    </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;